import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { API_BASE_URL } from '~/config';
import { useAuthStore } from '~/stores/auth';

type RefreshResponse = { access: string; refresh?: string };

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setTokens, clearSession } = useAuthStore.getState();
  if (!refreshToken) {
    clearSession();
    return null;
  }

  try {
    const response = await axios.post<RefreshResponse>(
      `${API_BASE_URL}/auth/api/auth/token/refresh/`,
      { refresh: refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );
    const nextAccess = response.data.access;
    const nextRefresh = response.data.refresh ?? refreshToken;
    setTokens({ accessToken: nextAccess, refreshToken: nextRefresh });
    return nextAccess;
  } catch {
    clearSession();
    return null;
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined;
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const nextAccess = await refreshPromise;
    if (!nextAccess) {
      return Promise.reject(error);
    }

    originalRequest.headers.Authorization = `Bearer ${nextAccess}`;
    return apiClient.request(originalRequest);
  },
);
